import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Link,
} from "@mui/material";
import {
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircle,
} from "@mui/icons-material";
import { resetPassword, clearError } from "../../store/slices/authSlice";
import Toast from "../../components/common/Toast";

const validationSchema = Yup.object({
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*]/,
      "Password must contain at least one special character"
    )
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token: tokenFromParams } = useParams(); // Fallback for URL token
  const { loading, error } = useSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");

  // Get token from location state (from OTP verification) or URL params (direct link)
  const token = location.state?.token || tokenFromParams;
  const email = location.state?.email;
  const otpVerified = location.state?.otpVerified; // Flag from OTP verification

  // Only redirect to forgot-password if:
  // 1. No token AND not coming from OTP verification (otpVerified flag)
  // 2. This prevents redirecting users who just verified OTP
  useEffect(() => {
    if (!token && !otpVerified && !tokenFromParams) {
      console.warn('No token found for password reset and not from OTP verification');
      // Small delay to allow any error messages to be displayed
      const timer = setTimeout(() => {
        navigate("/forgot-password", { 
          replace: true,
          state: { 
            error: "Invalid or missing reset token. Please request a new password reset." 
          } 
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [token, otpVerified, tokenFromParams, navigate]);

  // Clear error on unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      // Send email, newPassword, and confirmPassword in the request body
      const resetData = {
        email: email || "",
        newPassword: values.password,
        confirmPassword: values.confirmPassword,
        ...(token && token !== "otp-verified" && { token }), // Include token if available
      };

      const result = await dispatch(resetPassword(resetData));

      // Success
      if (resetPassword.fulfilled.match(result)) {
        const msg =
          result?.payload?.message || "Password reset successfully";

        setToastMessage(msg);
        setToastSeverity("success");
        setSuccess(true);

        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }

      // Failure
      if (resetPassword.rejected.match(result)) {
        const msg =
          result.payload ||
          result.error?.message ||
          result.error?.response?.data?.message ||
          "Failed to reset password";

        setToastMessage(msg);
        setToastSeverity("error");
      }
    },
  });

  const handleCloseToast = () => {
    setToastMessage(null);
  };

  // Allow rendering if:
  // 1. We have a token (from URL or state)
  // 2. OR we came from OTP verification (otpVerified flag)
  // This prevents redirect loop when coming from OTP verification
  if (!token && !otpVerified && !tokenFromParams) {
    return null; // Will redirect in useEffect
  }

  if (success) {
    return (
      <Box sx={{ width: "100%", textAlign: "center" }}>
        <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
        <Typography variant="h5" fontWeight={600} mb={2}>
          Password Reset Successful
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Your password has been reset successfully. You will be redirected to
          the login page shortly.
        </Typography>
        <Link
          component={RouterLink}
          to="/login"
          variant="body2"
          underline="hover"
        >
          Click here if you're not redirected
        </Link>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: "100%" }}>
      <Typography variant="h5" textAlign="center" fontWeight={600} mb={1}>
        Reset Password
      </Typography>
      <Typography
        variant="body2"
        textAlign="center"
        color="text.secondary"
        mb={3}
      >
        {email ? `Enter your new password for ${email}` : "Enter your new password below."}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        id="password"
        name="password"
        label="New Password *"
        type={showPassword ? "text" : "password"}
        placeholder="Enter new password"
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.password && Boolean(formik.errors.password)}
        helperText={formik.touched.password && formik.errors.password}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        id="confirmPassword"
        name="confirmPassword"
        label="Confirm Password *"
        type={showConfirmPassword ? "text" : "password"}
        placeholder="Confirm new password"
        value={formik.values.confirmPassword}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={
          formik.touched.confirmPassword &&
          Boolean(formik.errors.confirmPassword)
        }
        helperText={
          formik.touched.confirmPassword && formik.errors.confirmPassword
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Password must contain:
        </Typography>
        <ul style={{ margin: "8px 0", paddingLeft: 20 }}>
          <li>
            <Typography variant="caption">At least 8 characters</Typography>
          </li>
          <li>
            <Typography variant="caption">One uppercase letter</Typography>
          </li>
          <li>
            <Typography variant="caption">One lowercase letter</Typography>
          </li>
          <li>
            <Typography variant="caption">One number</Typography>
          </li>
          <li>
            <Typography variant="caption">
              One special character (!@#$%^&*)
            </Typography>
          </li>
        </ul>
      </Box>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        sx={{ py: 1.5 }}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          "Reset Password"
        )}
      </Button>

      {/* Top-right toast for reset password response */}
      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </Box>
  );
};

export default ResetPassword;
